import { DateTime } from "luxon";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import { VideoReel } from "@app/analytics/MeetingReport/types";

interface VideoState {
  // values
  /** Whether clicking a turn, highlight, etc. will autoplay the video */
  autoPlay: boolean;
  /** timestamp in seconds; auto-updated by the video player as it progresses */
  currentTimestamp: number;
  /** Whether highlight annotations should display on the video */
  highlightAnnotationsEnabled: boolean;
  highlightReel: VideoReel | null;
  /** Whether metric annotations (RS/E/S) should display on the video */
  metricAnnotationsEnabled: boolean;
  muted: boolean;
  /** if video is currently playing or paused */
  paused: boolean;
  /** if the video is available for playback */
  playbackEnabled: boolean;
  playbackRate: number;
  /** Current video reel that's playing; could be a highlight reel or a participant reel */
  videoReel: VideoReel | null;
  videoReelClipIndex: number | null;
  /** "jump to" timestamp; will be set in response to actions such as clicking on a transcript turn */
  videoStartAt: number | null;
  /** Offset in seconds between the meeting report start time and the start time
   * of the video recording */
  videoStartTimeOffset: number;
  /** start timestamp of the video recording */
  videoStartTime: DateTime | null;
  /** Whether the highlight reel has already been viewed */
  watchedHighlights: boolean;
  trailer: {
    watched: boolean;
    active: boolean;
    autoStart: boolean;
    url: string | null;
  };

  // actions
  resetState: () => void;
  startHighlightReel: () => void;
  startRecording: (videoStartAt?: number) => void;
  startTrailer: () => void;
  toggleAutoPlay: () => void;
  toggleHighlightAnnotations: () => void;
  toggleMetricAnnotations: () => void;

  // setters
  setCurrentTimestamp: (timestamp: number) => void;
  setHighlightReel: (highlightReel: VideoReel | null) => void;
  setMuted: (muted: boolean) => void;
  setPaused: (paused: boolean) => void;
  setPlaybackEnabled: (playbackEnabled: boolean) => void;
  setPlaybackRate: (playbackRate: number) => void;
  setTrailerUrl: (url: string | null) => void;
  setVideoReel: (videoReel: VideoReel | null) => void;
  setVideoReelClipIndex: (clipIndex: number | null) => void;
  setVideoReelPlaying: (playing: boolean) => void;
  setVideoStartAt: (videoStartAt: number | null) => void;
  setVideoStartTimeOffset: (videoStartTimeOffset: number) => void;
  setVideoStartTime: (videoStartTime: DateTime | null) => void;
  setWatchedHighlights: (watchedHighlights: boolean) => void;
  setWatchedTrailer: (watched: boolean) => void;
}

const DEFAULT_STATE = {
  autoPlay: true,
  currentTimestamp: 0,
  highlightAnnotationsEnabled: true,
  highlightReel: null,
  metricAnnotationsEnabled: true,
  muted: false,
  paused: true,
  playbackEnabled: false,
  playbackRate: 1,
  videoReel: null,
  videoReelClipIndex: null,
  videoStartTimeOffset: 0,
  videoStartTime: null,
  videoStartAt: null,
  watchedHighlights: false,
  trailer: {
    watched: false,
    active: true,
    autoStart: false,
    url: null,
  },
};

const useVideoStore = createWithEqualityFn<VideoState>(
  (set) => ({
    ...DEFAULT_STATE,
    resetState: () =>
      set((state) => ({
        ...DEFAULT_STATE,
        // maintain setting state across meeting reports
        autoPlay: state.autoPlay,
        highlightAnnotationsEnabled: state.highlightAnnotationsEnabled,
        metricAnnotationsEnabled: state.metricAnnotationsEnabled,
        playbackRate: state.playbackRate,
      })),
    startHighlightReel: () =>
      set((state) =>
        state.highlightReel
          ? {
              trailer: { ...state.trailer, active: false },
              videoReelClipIndex: 0,
              videoReel: {
                ...state.highlightReel,
                playing: true,
              },
            }
          : {}
      ),
    startRecording: (videoStartAt?: number) =>
      set((state) => ({
        trailer: { ...state.trailer, active: false },
        videoStartAt: videoStartAt ?? 0,
      })),
    startTrailer: () =>
      set((state) => ({
        trailer: {
          ...state.trailer,
          active: true,
          autoStart: true,
        },
      })),

    setCurrentTimestamp: (currentTimestamp) => set({ currentTimestamp }),
    setHighlightReel: (highlightReel) => set({ highlightReel }),
    setMuted: (muted) => set({ muted }),
    setPaused: (paused) => set({ paused }),
    setPlaybackEnabled: (playbackEnabled) => set({ playbackEnabled }),
    setPlaybackRate: (playbackRate) => set({ playbackRate }),
    setTrailerUrl: (url) =>
      set((state) => {
        if (
          (state.trailer.url == null && url != null) ||
          (state.trailer.url != null && url == null)
        ) {
          return {
            trailer: {
              ...state.trailer,
              url,
              active: Boolean(url),
            },
          };
        }
        return state;
      }),
    setVideoReel: (videoReel) =>
      set((state) => ({
        videoReel,
        videoReelClipIndex: videoReel ? state.videoReelClipIndex : null,
      })),
    setVideoReelClipIndex: (videoReelClipIndex) =>
      set((state) => ({
        videoReelClipIndex,
        trailer: { ...state.trailer, active: false },
      })),
    setVideoReelPlaying: (playing) =>
      set((state) =>
        state.videoReel ? { videoReel: { ...state.videoReel, playing } } : {}
      ),
    setVideoStartAt: (videoStartAt) =>
      set((state) => ({
        videoStartAt,
        trailer: { ...state.trailer, active: false },
      })),
    setVideoStartTimeOffset: (videoStartTimeOffset) =>
      set({ videoStartTimeOffset }),
    setVideoStartTime: (videoStartTime) => set({ videoStartTime }),
    setWatchedHighlights: (watchedHighlights) => set({ watchedHighlights }),
    setWatchedTrailer: (watched) =>
      set((state) => ({
        trailer: { ...state.trailer, watched, active: false },
      })),
    toggleAutoPlay: () => set((state) => ({ autoPlay: !state.autoPlay })),
    toggleHighlightAnnotations: () =>
      set((state) => ({
        highlightAnnotationsEnabled: !state.highlightAnnotationsEnabled,
      })),
    toggleMetricAnnotations: () =>
      set((state) => ({
        metricAnnotationsEnabled: !state.metricAnnotationsEnabled,
      })),
  }),
  shallow
);

export default useVideoStore;
